import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Key, Zap, Globe, ChevronRight, Check, Copy, ExternalLink,
  ArrowRight, AlertCircle, BookOpen, Layers, Play,
  Star, Clock, Shield, Activity
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Helpers ───────────────────────────────────────────────────────────────────
function StepBadge({ n }) {
  return (
    <span className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
      style={{ background: 'rgba(201,168,76,0.15)', color: '#c9a84c', border: '1px solid rgba(201,168,76,0.3)' }}>
      {n}
    </span>
  );
}

function SectionHeader({ icon: Icon, title, subtitle, color = '#c9a84c' }) {
  return (
    <div className="flex items-start gap-4 mb-8">
      <div className="w-12 h-12 rounded-sm flex items-center justify-center flex-shrink-0"
        style={{ background: `${color}15`, border: `1px solid ${color}30` }}>
        <Icon size={22} style={{ color }} />
      </div>
      <div>
        <h2 className="text-lg font-medium mb-1" style={{ color: 'var(--lux-text)' }}>{title}</h2>
        <p className="text-sm" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>{subtitle}</p>
      </div>
    </div>
  );
}

function InfoBox({ icon: Icon = AlertCircle, children, color = '#c9a84c' }) {
  return (
    <div className="flex gap-3 p-4 rounded-sm mb-6"
      style={{ background: `${color}08`, border: `1px solid ${color}20` }}>
      <Icon size={16} style={{ color, flexShrink: 0, marginTop: 2 }} />
      <p className="text-sm" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.7 }}>{children}</p>
    </div>
  );
}

function CodeSnippet({ code }) {
  const copy = () => { navigator.clipboard.writeText(code); toast.success('Copiato!'); };
  return (
    <div className="rounded-sm overflow-hidden my-3" style={{ border: '1px solid var(--lux-border-2)' }}>
      <div className="flex items-center justify-between px-3 py-2" style={{ background: '#111' }}>
        <span className="text-[10px] uppercase tracking-widest" style={{ color: 'rgba(229,229,229,0.3)' }}>Esempio</span>
        <button onClick={copy} className="flex items-center gap-1 text-[10px] transition-colors"
          style={{ color: 'rgba(229,229,229,0.4)', background: 'none', border: 'none', cursor: 'pointer' }}>
          <Copy size={10} /> Copia
        </button>
      </div>
      <pre className="px-4 py-3 text-xs font-mono overflow-x-auto"
        style={{ background: '#0a0a0a', color: '#a8b5c8' }}>{code}</pre>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      className="px-4 py-2 text-sm transition-all"
      style={{
        background: active ? 'rgba(201,168,76,0.1)' : 'transparent',
        color: active ? '#c9a84c' : 'var(--lux-text-muted)',
        borderBottom: active ? '2px solid #c9a84c' : '2px solid transparent',
        border: 'none',
        cursor: 'pointer',
      }}>
      {children}
    </button>
  );
}

// ── Widget Stato Connessioni ────────────────────────────────────────────────────
function StatusWidget({ token, tenantSlug }) {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusMap, setStatusMap] = useState({});

  const headers = {
    Authorization: `Bearer ${token}`,
    'X-Tenant-Slug': tenantSlug,
  };

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    axios.get(`${API}/api/webhooks`, { headers })
      .then(async r => {
        const whs = r.data.webhooks || [];
        setWebhooks(whs);
        // Per ogni webhook carica gli ultimi log
        const map = {};
        await Promise.all(whs.map(async wh => {
          try {
            const logRes = await axios.get(`${API}/api/webhooks/${wh.webhook_id}/logs?limit=5`, { headers });
            const logs = logRes.data.logs || [];
            const lastSuccess = logs.find(l => l.success);
            const lastFail = logs.find(l => !l.success);
            const successRate = logs.length > 0 ? Math.round((logs.filter(l => l.success).length / logs.length) * 100) : null;
            map[wh.webhook_id] = {
              total: logs.length,
              successRate,
              lastEvent: logs[0]?.event || null,
              lastAt: logs[0]?.triggered_at || null,
              ok: logs.length === 0 || (successRate !== null && successRate > 0),
            };
          } catch { map[wh.webhook_id] = { total: 0, successRate: null, ok: true }; }
        }));
        setStatusMap(map);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, tenantSlug]);

  return (
    <div data-testid="status-widget">
      <SectionHeader
        icon={Activity}
        color="#22c55e"
        title="Stato Connessioni"
        subtitle="Monitoraggio in tempo reale delle tue integrazioni webhook. Controllo rapido senza entrare nei log."
      />

      {loading ? (
        <div className="py-8 text-center text-sm" style={{ color: 'var(--lux-text-muted)' }}>
          <div className="w-5 h-5 border-2 rounded-full animate-spin mx-auto mb-2" style={{ borderColor: '#22c55e', borderTopColor: 'transparent' }} />
          Caricamento stato connessioni...
        </div>
      ) : webhooks.length === 0 ? (
        <div className="p-6 rounded-sm text-center" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
          <Globe size={28} className="mx-auto mb-3" style={{ color: 'var(--lux-text-muted-3)' }} />
          <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>Nessun webhook configurato</p>
          <p className="text-xs mb-4" style={{ color: 'var(--lux-text-muted)' }}>
            Configura il tuo primo webhook per ricevere notifiche in tempo reale.
          </p>
          <button onClick={() => onNavigate?.('webhooks')}
            className="flex items-center gap-2 px-4 py-2 text-sm mx-auto"
            style={{ background: '#a78bfa', color: '#000', border: 'none', cursor: 'pointer' }}>
            Configura Webhook <ArrowRight size={13} />
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {webhooks.map(wh => {
            const s = statusMap[wh.webhook_id];
            const isOk = wh.is_active && (s?.ok !== false);
            const isInactive = !wh.is_active;
            const hasProblems = wh.is_active && s?.ok === false;
            return (
              <div key={wh.webhook_id} className="flex items-center gap-4 p-4 rounded-sm"
                style={{ background: 'var(--lux-surface-3)', border: `1px solid ${hasProblems ? 'rgba(239,68,68,0.25)' : 'var(--lux-border-2)'}` }}>
                {/* Indicatore stato */}
                <div className="flex-shrink-0">
                  <div className="w-3 h-3 rounded-full"
                    style={{ background: isInactive ? '#6b7280' : hasProblems ? '#ef4444' : '#22c55e', boxShadow: isOk && !isInactive ? '0 0 6px #22c55e80' : 'none' }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--lux-text)' }}>{wh.label || wh.url}</p>
                    {isInactive && <span className="text-[10px] px-1.5 py-0.5 rounded" style={{ background: 'rgba(107,114,128,0.15)', color: '#9ca3af' }}>Disabilitato</span>}
                  </div>
                  <p className="text-xs truncate" style={{ color: 'var(--lux-text-muted-3)' }}>{wh.url}</p>
                </div>
                <div className="flex-shrink-0 text-right">
                  {s?.total > 0 ? (
                    <>
                      <p className="text-sm font-medium" style={{ color: s?.successRate >= 80 ? '#22c55e' : s?.successRate >= 50 ? '#f59e0b' : '#ef4444' }}>
                        {s?.successRate}%
                      </p>
                      <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>successo (ult. 5)</p>
                    </>
                  ) : (
                    <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>Nessun evento</p>
                  )}
                </div>
                {s?.lastEvent && (
                  <div className="flex-shrink-0 text-right hidden sm:block">
                    <p className="text-[10px] font-mono" style={{ color: '#a78bfa' }}>{s.lastEvent}</p>
                    <p className="text-[10px]" style={{ color: 'var(--lux-text-muted-3)' }}>
                      {s.lastAt ? new Date(s.lastAt).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-6 p-3 rounded-sm text-xs" style={{ background: 'rgba(34,197,94,0.05)', border: '1px solid rgba(34,197,94,0.15)' }}>
        <span style={{ color: '#22c55e', fontWeight: 600 }}>Tip: </span>
        <span style={{ color: 'var(--lux-text-muted)' }}>
          Verde = connessione attiva e funzionante. Rosso = errori recenti — vai alla sezione Webhook → Log → Riprova.
        </span>
      </div>
    </div>
  );
}

// ── Sezione: Cos'è l'API Key ──────────────────────────────────────────────────
function ApiKeyGuide({ onNavigate }) {
  return (
    <div>
      <SectionHeader
        icon={Key}
        title="API Key — La chiave digitale della tua struttura"
        subtitle="Permette ad altri programmi (Zapier, Make, il tuo PMS) di leggere e scrivere dati in Shangrilux in modo sicuro e automatico."
      />

      <InfoBox icon={Star}>
        <strong style={{ color: 'var(--lux-text)' }}>Analogia semplice:</strong> L'API Key è come il codice PIN del tuo bancomat digitale.
        Chi ce l'ha può accedere ai dati della tua struttura. Tienila riservata — non condividerla mai via email o chat!
      </InfoBox>

      {/* Come si genera */}
      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#c9a84c' }}>Come generare la tua prima chiave API</p>
      <div className="space-y-4 mb-8">
        {[
          { n: 1, title: 'Vai su "API Keys" nella sidebar sinistra', desc: 'Trovi la sezione nella parte bassa del menu di navigazione.' },
          { n: 2, title: 'Dai un nome alla chiave', desc: 'Usa un nome descrittivo che ricordi a cosa serve — es. "Zapier Prenotazioni" o "PMS Mews". Puoi avere più chiavi, una per ogni integrazione.' },
          { n: 3, title: 'Clicca "Genera Chiave"', desc: 'La chiave appare una sola volta. Copiala subito e salvala in un posto sicuro (es. le note del tuo password manager).' },
          { n: 4, title: 'Usa la chiave nel tuo strumento esterno', desc: 'Incollala in Zapier, Make o nel tuo PMS quando ti viene chiesta la "API Key" o la "Chiave di autenticazione".' },
        ].map(({ n, title, desc }) => (
          <div key={n} className="flex gap-4 p-4 rounded-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
            <StepBadge n={n} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>{title}</p>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Sicurezza */}
      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#c9a84c' }}>Sicurezza — cosa fare e non fare</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
        {[
          { ok: true,  text: 'Crea una chiave separata per ogni strumento' },
          { ok: true,  text: 'Revoca subito una chiave se pensi sia stata compromessa' },
          { ok: true,  text: 'Controlla l\'utilizzo mensile dal grafico in app' },
          { ok: false, text: 'Non inviare la chiave via WhatsApp o email' },
          { ok: false, text: 'Non usare la stessa chiave per più integrazioni diverse' },
          { ok: false, text: 'Non condividerla con collaboratori non autorizzati' },
        ].map(({ ok, text }) => (
          <div key={text} className="flex items-start gap-2 p-3 rounded-sm"
            style={{ background: ok ? 'rgba(34,197,94,0.05)' : 'rgba(239,68,68,0.05)', border: `1px solid ${ok ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)'}` }}>
            {ok
              ? <Check size={13} className="mt-0.5 flex-shrink-0" style={{ color: '#22c55e' }} />
              : <span className="text-xs font-bold flex-shrink-0 mt-0.5" style={{ color: '#ef4444' }}>✕</span>
            }
            <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{text}</p>
          </div>
        ))}
      </div>

      <button onClick={() => onNavigate('api-keys')}
        data-testid="guide-goto-apikeys"
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all"
        style={{ background: 'var(--lux-gold)', color: '#000', border: 'none', cursor: 'pointer', borderRadius: 0 }}>
        Vai alle API Keys <ArrowRight size={15} />
      </button>
    </div>
  );
}

// ── Sezione: Webhook ──────────────────────────────────────────────────────────
function WebhookGuide({ onNavigate }) {
  return (
    <div>
      <SectionHeader
        icon={Globe}
        color="#a78bfa"
        title="Webhook — Notifiche automatiche in tempo reale"
        subtitle="Un webhook è come un avviso automatico: ogni volta che succede qualcosa in Shangrilux (nuova prenotazione, check-in...) viene inviata una notifica istantanea al tuo strumento."
      />

      <InfoBox icon={Star} color="#a78bfa">
        <strong style={{ color: 'var(--lux-text)' }}>Analogia semplice:</strong> Immagina di avere un campanello intelligente sulla porta della tua struttura.
        Ogni volta che arriva un ospite, il campanello suona automaticamente e invia un avviso al tuo telefono, al PMS e a qualsiasi altro sistema tu voglia.
        Il webhook fa esattamente questo — ma con i dati digitali.
      </InfoBox>

      {/* Differenza API / Webhook */}
      <div className="grid grid-cols-2 gap-3 mb-8">
        <div className="p-4 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Key size={14} style={{ color: '#c9a84c' }} />
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: '#c9a84c' }}>API Key</p>
          </div>
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--lux-text)' }}>Tu chiedi</strong> ad Shangrilux i dati quando ti servono.
            <br /><br />Es. ogni mattina il tuo PMS scarica le prenotazioni del giorno.
          </p>
        </div>
        <div className="p-4 rounded-sm" style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
          <div className="flex items-center gap-2 mb-2">
            <Globe size={14} style={{ color: '#a78bfa' }} />
            <p className="text-xs font-medium uppercase tracking-wider" style={{ color: '#a78bfa' }}>Webhook</p>
          </div>
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>
            <strong style={{ color: 'var(--lux-text)' }}>Shangrilux ti avvisa</strong> automaticamente appena succede qualcosa.
            <br /><br />Es. arriva subito una notifica Slack quando un ospite fa il check-in.
          </p>
        </div>
      </div>

      {/* Come configurare */}
      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#a78bfa' }}>Come configurare il tuo primo webhook</p>
      <div className="space-y-4 mb-8">
        {[
          {
            n: 1, title: 'Ottieni un "URL destinazione" dal tuo strumento',
            desc: 'In Zapier si chiama "Webhook URL", in Make si chiama "URL di ascolto". Lo crei nel passaggio iniziale del tuo flusso automatico e ha un formato simile a https://hooks.zapier.com/...',
          },
          {
            n: 2, title: 'Vai su "Webhook" nella sidebar e clicca "Aggiungi Webhook"',
            desc: 'Assegna un nome (es. "Zapier - Nuove prenotazioni"), incolla l\'URL che hai copiato e scegli quali eventi vuoi ricevere.',
          },
          {
            n: 3, title: 'Scegli gli eventi che ti interessano',
            desc: 'Puoi selezionare solo ciò che ti serve: nuove prenotazioni, check-in confermati, comandi serratura, ecc. Non è necessario attivarli tutti.',
          },
          {
            n: 4, title: 'Premi "Test" per verificare che tutto funzioni',
            desc: 'Shangrilux invia un messaggio di prova al tuo strumento. Se lo ricevi correttamente, il webhook è configurato. Altrimenti controlla l\'URL e riprova.',
          },
          {
            n: 5, title: 'Monitora i log delle consegne',
            desc: 'Dal pannello Webhook puoi vedere ogni notifica inviata: se è andata a buon fine (verde) o ha avuto un problema (rosso). In caso di errore, clicca "Riprova" per ripetere l\'invio.',
          },
        ].map(({ n, title, desc }) => (
          <div key={n} className="flex gap-4 p-4 rounded-sm"
            style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
            <StepBadge n={n} />
            <div>
              <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>{title}</p>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Eventi */}
      <p className="text-xs uppercase tracking-widest mb-4" style={{ color: '#a78bfa' }}>Quali eventi puoi ricevere</p>
      <div className="rounded-sm overflow-hidden mb-8" style={{ border: '1px solid var(--lux-border-2)' }}>
        {[
          { evento: 'Nuova prenotazione', quando: 'Ogni volta che arriva una nuova richiesta di soggiorno', icon: '📋' },
          { evento: 'Prenotazione confermata', quando: 'Quando lo staff accetta la richiesta dell\'ospite', icon: '✅' },
          { evento: 'Prenotazione cancellata', quando: 'Quando una prenotazione viene annullata', icon: '❌' },
          { evento: 'Check-in effettuato', quando: 'Quando l\'ospite apre la serratura al check-in', icon: '🔑' },
          { evento: 'Comando accesso', quando: 'Ogni apertura/chiusura serratura da remoto o di persona', icon: '🚪' },
          { evento: 'Chiave ospite generata', quando: 'Quando viene creato e inviato un QR code o link di accesso', icon: '📱' },
        ].map(({ evento, quando, icon }, i) => (
          <div key={evento} className="flex items-center gap-4 px-4 py-3"
            style={{ background: i % 2 === 0 ? 'var(--lux-surface-3)' : 'transparent', borderBottom: '1px solid var(--lux-border-2)' }}>
            <span className="text-base w-6 flex-shrink-0">{icon}</span>
            <div className="flex-1">
              <p className="text-xs font-medium" style={{ color: 'var(--lux-text)' }}>{evento}</p>
              <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{quando}</p>
            </div>
          </div>
        ))}
      </div>

      <button onClick={() => onNavigate('webhooks')}
        data-testid="guide-goto-webhooks"
        className="flex items-center gap-2 px-5 py-2.5 text-sm font-medium transition-all"
        style={{ background: '#a78bfa', color: '#000', border: 'none', cursor: 'pointer', borderRadius: 0 }}>
        Vai ai Webhook <ArrowRight size={15} />
      </button>
    </div>
  );
}

// ── Sezione: Integrazioni pratiche ─────────────────────────────────────────────
function IntegrationsGuide() {
  const [active, setActive] = useState('zapier');

  const integrations = {
    zapier: {
      label: 'Zapier',
      color: '#ff4a00',
      logo: 'Z',
      subtitle: 'Il modo più semplice per collegare Shangrilux a centinaia di strumenti senza scrivere codice.',
      useCases: [
        'Ricevi una email quando arriva una nuova prenotazione',
        'Aggiungi automaticamente l\'ospite in un foglio Google Sheets',
        'Invia un messaggio WhatsApp o SMS all\'ospite con i dettagli del check-in',
        'Crea un evento in Google Calendar per ogni prenotazione confermata',
        'Notifica il team su Slack ad ogni check-in',
      ],
      steps: [
        { title: 'Crea un account su zapier.com (gratuito per iniziare)', desc: 'Vai su zapier.com e registrati. Non serve carta di credito per il piano base.' },
        { title: 'Crea un nuovo "Zap"', desc: 'Clicca su "Create Zap". Questo è il tuo flusso automatico.' },
        { title: 'Scegli "Webhook by Zapier" come trigger', desc: 'Cerca "Webhook" nella lista degli strumenti. Seleziona "Catch Hook" e copia l\'URL che Zapier ti dà.' },
        { title: 'Configura il webhook in Shangrilux', desc: 'Nel pannello Shangrilux → Webhook → Aggiungi Webhook. Incolla l\'URL di Zapier. Seleziona l\'evento che ti interessa (es. "Nuova prenotazione").' },
        { title: 'Testa la connessione', desc: 'In Shangrilux clicca "Test". Torna su Zapier e clicca "Test trigger" — dovresti vedere i dati arrivare.' },
        { title: 'Aggiungi l\'azione (es. invia email)', desc: 'Scegli cosa vuoi fare con il dato ricevuto: inviare una email, scrivere su un foglio, mandare un messaggio, ecc.' },
        { title: 'Attiva il Zap', desc: 'Clicca "Publish". Da questo momento il flusso è attivo e lavora in automatico 24/7.' },
      ],
    },
    make: {
      label: 'Make (ex Integromat)',
      color: '#6d00cc',
      logo: 'M',
      subtitle: 'Strumento potente per automazioni più complesse. Ottimo se vuoi collegare più sistemi tra loro.',
      useCases: [
        'Sincronizza le prenotazioni in tempo reale con il tuo PMS',
        'Genera automaticamente una fattura PDF per ogni soggiorno',
        'Aggiorna l\'inventario camere su Booking.com o Airbnb',
        'Invia report automatici settimanali al proprietario',
        'Archivia automaticamente i dati ospiti nel tuo CRM',
      ],
      steps: [
        { title: 'Crea un account su make.com', desc: 'Piano gratuito disponibile su make.com. Non serve carta di credito.' },
        { title: 'Crea uno "Scenario"', desc: 'Uno scenario è il flusso di automazione. Clicca "Create new scenario".' },
        { title: 'Aggiungi il modulo "Webhooks"', desc: 'Cerca e aggiungi il modulo "Webhooks" → "Custom webhook". Make ti fornisce un URL univoco.' },
        { title: 'Configura il webhook in Shangrilux', desc: 'Nel pannello Shangrilux → Webhook → Aggiungi Webhook. Incolla l\'URL di Make. Scegli l\'evento.' },
        { title: 'Esegui il test e determina la struttura', desc: 'In Shangrilux clicca "Test". Torna su Make e clicca "Run once" — Make riceve i dati e capisce automaticamente la struttura.' },
        { title: 'Aggiungi i moduli successivi', desc: 'Collega altri strumenti nel flusso: Google Sheets, Gmail, un CRM, il tuo gestionale, ecc.' },
        { title: 'Attiva lo scenario', desc: 'Imposta la frequenza di esecuzione e attiva. Lo scenario gira automaticamente.' },
      ],
    },
    sheets: {
      label: 'Google Sheets',
      color: '#0f9d58',
      logo: 'G',
      subtitle: 'Tieni un registro automatico di tutte le prenotazioni su un foglio Google aggiornato in tempo reale.',
      useCases: [
        'Registro prenotazioni aggiornato in automatico — niente inserimento manuale',
        'Dashboard semplice con entrate previste per mese',
        'Elenco ospiti con dati di contatto sempre aggiornato',
        'Report settimanale del tasso di occupazione',
      ],
      steps: [
        { title: 'Crea un foglio Google Sheets con le colonne', desc: 'Apri Google Sheets e crea un nuovo foglio con le intestazioni: Data, Ospite, Camera, Check-in, Check-out, Totale.' },
        { title: 'Usa Zapier o Make per collegare i due sistemi', desc: 'Zapier e Make hanno entrambi un\'integrazione nativa con Google Sheets. Non serve alcun codice.' },
        { title: 'In Zapier: crea un Zap "Shangrilux Webhook → Aggiungi riga Google Sheets"', desc: 'Webhook di Shangrilux come trigger, "Add Row in Google Sheets" come azione. Collega i campi: nome ospite, date, camera, ecc.' },
        { title: 'Testa con una prenotazione reale', desc: 'Crea una prenotazione di prova in Shangrilux. Verifica che appaia nel foglio Google entro pochi secondi.' },
      ],
    },
    pms: {
      label: 'PMS / Gestionale',
      color: '#0ea5e9',
      logo: 'P',
      subtitle: 'Collega Shangrilux al tuo sistema di gestione alberghiera (Mews, Opera, Cloudbeds, o qualsiasi altro).',
      useCases: [
        'Sincronizzazione automatica prenotazioni tra Shangrilux e il PMS',
        'Aggiornamento disponibilità camere in tempo reale',
        'Invio automatico dei dati ospite al PMS al check-in',
        'Fatturazione automatica al momento del checkout',
      ],
      steps: [
        { title: 'Verifica se il tuo PMS ha un\'integrazione nativa', desc: 'Alcuni PMS (come Mews e Cloudbeds) supportano direttamente le API Shangrilux. Contatta il supporto del tuo PMS chiedendo "API Key integration".' },
        { title: 'Se non esiste, usa Make come ponte', desc: 'Make può fare da intermediario tra Shangrilux e il tuo PMS, trasformando i dati nel formato richiesto.' },
        { title: 'Genera una API Key dedicata in Shangrilux', desc: 'Vai su API Keys → "Genera Chiave" → nomina la chiave con il nome del tuo PMS (es. "Mews Sync").' },
        { title: 'Fornisci la chiave al responsabile IT del PMS', desc: 'La chiave API e l\'URL base (' + API + '/api/v1) sono tutto ciò che serve al tuo PMS per connettersi.' },
        { title: 'Configura un webhook per notifiche real-time', desc: 'Il PMS può anche ricevere notifiche push tramite webhook. Chiedi al supporto del PMS l\'URL da configurare.' },
      ],
    },
  };

  const current = integrations[active];

  return (
    <div>
      <SectionHeader
        icon={Layers}
        color="#22c55e"
        title="Integrazioni pratiche — Collega Shangrilux ai tuoi strumenti"
        subtitle="Non serve saper programmare. Con Zapier e Make puoi collegare Shangrilux a qualsiasi strumento in pochi minuti."
      />

      {/* Tab selezione */}
      <div className="flex gap-0 mb-6 border-b" style={{ borderColor: 'var(--lux-border-2)' }}>
        {Object.entries(integrations).map(([key, val]) => (
          <TabButton key={key} active={active === key} onClick={() => setActive(key)}>
            {val.label}
          </TabButton>
        ))}
      </div>

      {/* Contenuto tab */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center font-bold text-white"
            style={{ background: current.color }}>
            {current.logo}
          </div>
          <div>
            <p className="font-medium" style={{ color: 'var(--lux-text)' }}>{current.label}</p>
            <p className="text-xs" style={{ color: 'var(--lux-text-muted)' }}>{current.subtitle}</p>
          </div>
        </div>

        {/* Use cases */}
        <p className="text-xs uppercase tracking-widest mb-3" style={{ color: current.color }}>Cosa puoi fare</p>
        <div className="grid gap-2 mb-6">
          {current.useCases.map(uc => (
            <div key={uc} className="flex items-start gap-2">
              <Check size={13} className="mt-0.5 flex-shrink-0" style={{ color: current.color }} />
              <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>{uc}</p>
            </div>
          ))}
        </div>

        {/* Passi */}
        <p className="text-xs uppercase tracking-widest mb-3" style={{ color: current.color }}>Guida passo per passo</p>
        <div className="space-y-3">
          {current.steps.map(({ title, desc }, i) => (
            <div key={i} className="flex gap-4 p-4 rounded-sm"
              style={{ background: 'var(--lux-surface-3)', border: '1px solid var(--lux-border-2)' }}>
              <StepBadge n={i + 1} />
              <div>
                <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>{title}</p>
                <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>{desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Sezione: Problemi comuni ────────────────────────────────────────────────────
function TroubleshootGuide({ onNavigate }) {
  const problems = [
    {
      q: 'Il test del webhook non funziona — cosa faccio?',
      a: [
        'Verifica che l\'URL incollato sia corretto (niente spazi all\'inizio o alla fine)',
        'Assicurati che il tuo strumento (Zapier, Make) stia "ascoltando" — in Zapier devi aver cliccato "Test trigger" prima di inviare il test',
        'Controlla il Log Delivery nel pannello Webhook per vedere il codice di errore',
        'Se vedi "timeout", il tuo strumento potrebbe aver impiegato troppo. Prova a ri-testare',
      ],
    },
    {
      q: 'La mia API Key non funziona più',
      a: [
        'Verifica di non aver superato il limite di richieste orarie (controlla il grafico in API Keys)',
        'Controlla se la chiave è stata revocata per errore — in quel caso genera una nuova chiave',
        'Assicurati che la chiave venga passata nell\'intestazione corretta: X-API-Key: [la tua chiave]',
        'Se il problema persiste, contatta il supporto con il nome della chiave (es. "Zapier Prenotazioni")',
      ],
    },
    {
      q: 'Non ricevo le notifiche webhook',
      a: [
        'Verifica che il webhook sia attivo (non in stato "Disabilitato") nel pannello',
        'Controlla che l\'evento che aspetti sia tra quelli selezionati nel webhook',
        'Guarda il Log Delivery — se i log mostrano consegne rosse, clicca "Riprova"',
        'Verifica che l\'URL di destinazione sia raggiungibile pubblicamente (non un indirizzo locale tipo localhost:3000)',
      ],
    },
    {
      q: 'Voglio ricevere le notifiche solo per una proprietà specifica',
      a: [
        'Al momento i webhook inviano notifiche per tutti gli eventi della struttura',
        'Nel payload che ricevi c\'è sempre l\'ID proprietà — nel tuo flusso Zapier/Make puoi aggiungere un filtro per gestire solo quella che ti interessa',
        'Contatta il supporto per configurazioni avanzate multi-proprietà',
      ],
    },
  ];

  return (
    <div>
      <SectionHeader
        icon={AlertCircle}
        color="#f59e0b"
        title="Problemi comuni e soluzioni"
        subtitle="Hai un problema con le integrazioni? Cerca qui la soluzione prima di contattare il supporto."
      />

      <div className="space-y-4">
        {problems.map(({ q, a }) => (
          <details key={q} className="group rounded-sm overflow-hidden"
            style={{ border: '1px solid var(--lux-border-2)' }}>
            <summary className="flex items-center justify-between px-5 py-4 cursor-pointer text-sm font-medium"
              style={{ background: 'var(--lux-surface-3)', color: 'var(--lux-text)', listStyle: 'none' }}>
              <span className="flex items-center gap-3">
                <AlertCircle size={15} style={{ color: '#f59e0b', flexShrink: 0 }} />
                {q}
              </span>
              <ChevronRight size={15} style={{ color: 'var(--lux-text-muted)', transition: 'transform 0.2s' }}
                className="group-open:rotate-90" />
            </summary>
            <div className="px-5 py-4" style={{ background: 'var(--lux-surface-1)' }}>
              <ul className="space-y-2">
                {a.map((step, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs"
                    style={{ color: 'var(--lux-text-muted)', lineHeight: 1.7 }}>
                    <span className="font-bold flex-shrink-0" style={{ color: '#f59e0b' }}>{i + 1}.</span>
                    {step}
                  </li>
                ))}
              </ul>
            </div>
          </details>
        ))}
      </div>

      <div className="mt-8 p-4 rounded-sm" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
        <p className="text-sm font-medium mb-1" style={{ color: 'var(--lux-text)' }}>Hai ancora bisogno di aiuto?</p>
        <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.7 }}>
          Consulta le guide tecniche complete nella sezione <a href="/blog" target="_blank"
            style={{ color: '#c9a84c', textDecoration: 'none' }}>Blog / Guide Integrazioni →</a>
          &nbsp;oppure scrivi al supporto. Il nostro team risponde entro 24 ore.
        </p>
      </div>
    </div>
  );
}

// ── Componente principale ────────────────────────────────────────────────────────
const SECTIONS = [
  { id: 'status',       label: 'Stato Connessioni', icon: Activity, color: '#22c55e' },
  { id: 'apikey',       label: 'API Key',            icon: Key,      color: '#c9a84c' },
  { id: 'webhook',      label: 'Webhook',            icon: Globe,    color: '#a78bfa' },
  { id: 'integrazioni', label: 'Integrazioni',       icon: Layers,   color: '#22c55e' },
  { id: 'problemi',     label: 'Problemi comuni',    icon: AlertCircle, color: '#f59e0b' },
];

export function Guide({ onNavigate, token, tenantSlug }) {
  const [activeSection, setActiveSection] = useState('status');

  return (
    <div data-testid="guide-section" className="flex gap-0 min-h-full">
      {/* Sidebar interna */}
      <div className="w-52 flex-shrink-0 border-r py-6"
        style={{ borderColor: 'var(--lux-border-1)', background: 'var(--lux-surface-2)' }}>
        <p className="px-5 text-[10px] uppercase tracking-[3px] mb-4"
          style={{ color: 'var(--lux-text-muted-3)' }}>Argomenti</p>
        {SECTIONS.map(({ id, label, icon: Icon, color }) => {
          const active = activeSection === id;
          return (
            <button key={id} onClick={() => setActiveSection(id)}
              data-testid={`guide-tab-${id}`}
              className="w-full flex items-center gap-3 px-5 py-3 text-sm text-left transition-all"
              style={{
                background: active ? `${color}10` : 'transparent',
                color: active ? color : 'var(--lux-text-muted)',
                borderRight: active ? `2px solid ${color}` : '2px solid transparent',
                border: 'none',
                borderRight: active ? `2px solid ${color}` : '2px solid transparent',
                cursor: 'pointer',
              }}>
              <Icon size={15} />
              {label}
            </button>
          );
        })}

        {/* Box aiuto rapido */}
        <div className="mx-4 mt-8 p-3 rounded-sm" style={{ background: 'rgba(201,168,76,0.06)', border: '1px solid rgba(201,168,76,0.15)' }}>
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: '#c9a84c' }}>Aiuto rapido</p>
          <p className="text-xs" style={{ color: 'var(--lux-text-muted)', lineHeight: 1.6 }}>
            Non riesci a configurare un'integrazione? Usa il pulsante "Test" per verificare che il webhook sia attivo.
          </p>
        </div>
      </div>

      {/* Contenuto */}
      <div className="flex-1 p-8 overflow-y-auto">
        {/* Header pagina */}
        <div className="mb-8">
          <h1 className="font-serif text-2xl font-light mb-2" style={{ color: 'var(--lux-text)' }}>
            Guida alle Integrazioni
          </h1>
          <p className="text-sm" style={{ color: 'var(--lux-text-muted)' }}>
            Tutto quello che ti serve per collegare Shangrilux ai tuoi strumenti — senza competenze tecniche.
          </p>
        </div>

        {activeSection === 'status'        && <StatusWidget token={token} tenantSlug={tenantSlug} />}
        {activeSection === 'apikey'         && <ApiKeyGuide onNavigate={onNavigate} />}
        {activeSection === 'webhook'        && <WebhookGuide onNavigate={onNavigate} />}
        {activeSection === 'integrazioni'   && <IntegrationsGuide />}
        {activeSection === 'problemi'       && <TroubleshootGuide onNavigate={onNavigate} />}
      </div>
    </div>
  );
}
